/* Download Template / Upload icons for the Weekly EAC forecast round-trip
   (docs/api/weekly-eac-template-download.md, weekly-eac-template-upload.md) —
   distinct from the plain "Download" export icon in Index.js
   (downloadWeeklyEACDirectLabor), which exports current data read-only rather
   than an editable .xlsx workbook.

   Rendered inline inside Index.js's shared top-right icon strip (the
   `classes.icons` div, position:relative Grid item) for pageType==="weeklyEAC"
   — same absolute right:72px/36px offsets as that strip's "detail" pageType
   icons (Download Template / Upload / plain Download at right:0), so all
   three line up identically regardless of pageType. Gating mirrors "detail"
   too: a read-only accessType only ever sees Download Template; Upload
   additionally requires "RW" access AND an Open version (the upload API
   itself rejects a non-Open version). `isEditable` (from GET .../rvn-clins,
   per-CLIN) is a further, independent gate on BOTH icons — some CLINs are
   flagged not editable regardless of the user's own access level, and the
   plain "Download" icon in Index.js is deliberately unaffected by it (that
   one stays available even for a non-editable CLIN). */
import React, { useState } from "react";
import Drawer from "@material-ui/core/Drawer";
import CloseIcon from "@material-ui/icons/Close";
import { makeStyles } from "@material-ui/core/styles";

import Loading from "../../../../common-components/Loading";
import FileUpload from "../../../../common-components/FileUpload";
import { DownloadTemplateIcon, UploadIcon } from "../../../../shared-resources/SVGIcons";
import { downloadFile } from "../../../../shared-resources/utils";
import {
  downloadWeeklyEACTemplateAPI,
  uploadWeeklyEACTemplateAPI,
} from "../../../../api/financial-management-api";

const useStyles = makeStyles((theme) => ({
  drawerSection: {
    width: "420px",
    position: "relative",
    padding: theme.spacing(0.75, 4),
  },
  closeSection: {
    position: "absolute",
    cursor: "pointer",
    right: theme.spacing(0.25),
    top: theme.spacing(0.25),
  },
  closeIcon: {
    color: "#C4C4C4",
  },
  title: {
    color: "#0082CA",
    fontSize: theme.spacing(2.25),
    marginBottom: theme.spacing(2),
  },
  description: {
    marginBottom: theme.spacing(2),
  },
  errorText: {
    color: "#c0392b",
    marginBottom: theme.spacing(2),
  },
}));

export default function WeeklyEACTemplateActions({
  contractId,
  clin,
  version,
  versionName,
  versionStatus,
  accessType,
  isEditable = true,
  onUploaded,
}) {
  const classes = useStyles();
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const isOpenVersion = (versionStatus || "").toLowerCase() === "open";
  const canDownloadTemplate = isEditable && isOpenVersion && (accessType === "R" || accessType === "RW");
  const canUpload = isEditable && isOpenVersion && accessType === "RW";

  if (!clin || !canDownloadTemplate) return null;

  const handleDownloadTemplate = () => {
    setDownloading(true);
    downloadWeeklyEACTemplateAPI(contractId, clin, version).then((res) => {
      setDownloading(false);
      if (res && res.type) {
        downloadFile(res, `WeeklyEAC_Template_${clin}_${versionName}`);
      }
    });
  };

  const handleUpload = (formData) => {
    setUploading(true);
    setErrorMsg("");
    uploadWeeklyEACTemplateAPI(contractId, clin, version, formData)
      .then((res) => {
        setUploading(false);
        if (res.status === 200) {
          if (res.blob) {
            downloadFile(res.blob, `WeeklyEAC_Upload_Result_${clin}_${versionName}`);
          }
          setOpenDrawer(false);
          if (onUploaded) onUploaded();
        } else {
          setErrorMsg(
            res.message || "Unable to process the uploaded file. Please check the file and try again.",
          );
        }
      })
      .catch(() => {
        setUploading(false);
        setErrorMsg("Something went wrong during the upload.");
      });
  };

  // Same right-offset scheme as the "detail" pageType's icon trio: template
  // download sits to the left of Upload when both show, or takes Upload's
  // own slot (right: 36px) when the user is read-only and Upload never
  // renders — never leaving a gap where Upload would have been.
  const templateRight = canUpload ? "72px" : "36px";

  return (
    <>
      <span
        style={{ cursor: "pointer", position: "absolute", right: templateRight, top: "12px" }}
        title="Download Template"
        onClick={handleDownloadTemplate}
      >
        <DownloadTemplateIcon color="#0070D2" />
      </span>
      {canUpload && (
        <span
          style={{ cursor: "pointer", position: "absolute", right: "36px", top: "4px" }}
          title="Upload"
          onClick={() => setOpenDrawer(true)}
        >
          <UploadIcon background="#0070D2" color="#fff" />
        </span>
      )}

      <Drawer anchor="right" open={openDrawer} onClose={() => setOpenDrawer(false)}>
        <div className={classes.drawerSection}>
          <div className={classes.title}>Bulk Upload</div>
          <div className={classes.description}>
            Please Drag and Drop or select the required files
          </div>
          <div className={classes.closeSection} onClick={() => setOpenDrawer(false)}>
            <CloseIcon className={classes.closeIcon} />
          </div>
          {errorMsg && <div className={classes.errorText}>{errorMsg}</div>}
          <FileUpload getFormData={handleUpload} />
        </div>
      </Drawer>

      {(downloading || uploading) && <Loading />}
    </>
  );
}
